import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { PictureAsset } from "@/components/site/PictureAsset";
import "@/components/site/ScenesOfHomes.css";

const BASES = {
  immersive: "/assets/homepage/scenes/01-immersive",
  indian:    "/assets/homepage/scenes/02-indian-rooted",
  details:   "/assets/homepage/scenes/03-details",
  kitchen:   "/assets/homepage/scenes/04-kitchen",
  wide:      "/assets/homepage/scenes/05-wide",
};

export default function ScenesOfHomes({ id = "scenes-section" }) {
  const reducedMotion = useReducedMotion();
  const sectionRef = useRef(null);
  const scene01Ref = useRef(null);
  const revealRefs = useRef([]);
  const [s1Shift, setS1Shift] = useState(38); // object-position Y %

  // Preload all Scenes section images. Scene 01 starts immediately on
  // mount (highest priority). Scenes 02–05 start when the section
  // before Scenes (Press) approaches the viewport, so the rest are
  // ready by the time the user gets there. Falls back to a small delay
  // if IntersectionObserver isn't available.
  //
  // Preload URLs target the WebP fallback tier for modern browsers.
  // Older browsers that don't understand WebP will silently 404 the
  // preload request (harmless) and fetch the JPEG via <picture> when
  // the image element mounts.
  useEffect(() => {
    const sources = [
      `${BASES.immersive}-1600.webp`,
      `${BASES.indian}-1600.webp`,
      `${BASES.details}-1600.webp`,
      `${BASES.kitchen}-1600.webp`,
      `${BASES.wide}-1600.webp`,
    ];
    // Scene 01 — preload immediately
    const first = new Image();
    first.src = sources[0];

    const preloadRest = () => {
      sources.slice(1).forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    };

    let io = null;
    let fallbackTimer = null;
    const trigger = document.querySelector('[data-testid="press-section"]');
    if (trigger && "IntersectionObserver" in window) {
      io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            preloadRest();
            io.disconnect();
            io = null;
          }
        },
        { rootMargin: "600px 0px 600px 0px", threshold: 0 }
      );
      io.observe(trigger);
    } else {
      fallbackTimer = setTimeout(preloadRest, 4000);
    }
    return () => {
      if (io) io.disconnect();
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, []);

  // IntersectionObserver — fade/reveal scenes + intro as they enter view
  useEffect(() => {
    const nodes = revealRefs.current.filter(Boolean);
    if (!nodes.length) return;
    if (reducedMotion || !("IntersectionObserver" in window)) {
      nodes.forEach((n) => n.classList.add("is-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" }
    );
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [reducedMotion]);

  // Scene 01 — subtle scroll-led vertical shift of object-position
  useEffect(() => {
    if (reducedMotion) return;
    const el = scene01Ref.current;
    if (!el) return;
    let rafId = null;
    const update = () => {
      rafId = null;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // Progress from when the top enters viewport (1) to when bottom leaves (0)
      const total = rect.height + vh;
      const traveled = Math.min(total, Math.max(0, vh - rect.top));
      const p = traveled / total; // 0 → 1
      // ~4% movement only — keep room composition stable: 38% → 42% Y
      const shift = 38 + p * 4;
      setS1Shift(shift);
    };
    const onScroll = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [reducedMotion]);

  const setReveal = (i) => (n) => {
    revealRefs.current[i] = n;
  };

  return (
    <section
      ref={sectionRef}
      id={id}
      className="scenes"
      data-testid="scenes-work-section"
      aria-label="Selected work — scenes from our homes"
    >
      <div className="scenes__paper" aria-hidden="true" />

      {/* ============================================================
          INTRO BLOCK
          ============================================================ */}
      <div
        ref={setReveal(0)}
        className="scenes__intro"
        data-testid="selected-work-section"
      >
        <span className="scenes__eyebrow">Selected Work</span>
        <h2 className="scenes__headline">Scenes from our homes</h2>
        <p className="scenes__subtext">
          A curated look at our homes, and the subtle poetry within everyday living.
        </p>
      </div>

      {/* ============================================================
          SCENE 01 — THE ARRIVAL (immersive full-bleed)
          ============================================================ */}
      <article
        ref={(n) => {
          setReveal(1)(n);
          scene01Ref.current = n;
        }}
        className="scenes__scene scenes__scene--immersive"
        data-testid="scene-01"
      >
        <div className="scenes__immersive-frame">
          <PictureAsset
            base={BASES.immersive}
            alt="Warm-lit entry with patterned wall, lamp, doorway, ceiling grid and carved panels"
            className="scenes__immersive-img"
            sizes="100vw"
            loading="eager"
            fetchPriority="high"
            style={{ objectPosition: `center ${s1Shift}%` }}
          />
          <div className="scenes__immersive-veil" aria-hidden="true" />
          <div className="scenes__caption scenes__caption--right scenes__caption--woodbox">
            <span className="scenes__num">Scene 01</span>
            <h3 className="scenes__title">The Arrival</h3>
            <p className="scenes__desc">
              Pattern, shadow, and warm light compose the first pause.
            </p>
            <a
              href="/work/in-the-warm-after"
              className="scenes__view"
              data-testid="scene-01-link"
            >
              View Residence
              <span aria-hidden="true" className="scenes__view-rule" />
            </a>
          </div>
        </div>
      </article>

      {/* ============================================================
          SCENE 02 — THE RITUAL (editorial, image left, caption right)
          ============================================================ */}
      <article
        ref={setReveal(2)}
        className="scenes__scene scenes__scene--split"
        data-testid="scene-02"
      >
        <div className="scenes__split scenes__split--image-left">
          <div className="scenes__mask scenes__mask--curtain">
            <PictureAsset
              base={BASES.indian}
              alt="Entry with chinoiserie artwork, arched door detail, carved panels and warm lamp glow"
              className="scenes__img"
              sizes="(min-width: 880px) 55vw, 100vw"
            />
          </div>
          <div className="scenes__caption scenes__caption--beside">
            <span className="scenes__num">Scene 02</span>
            <h3 className="scenes__title">The Ritual</h3>
            <p className="scenes__desc">
              Art, objects, and thresholds gather into a room that feels lived,
              remembered, and held.
            </p>
            <a
              href="/work/windows-to-art"
              className="scenes__view"
              data-testid="scene-02-link"
            >
              View Residence
              <span aria-hidden="true" className="scenes__view-rule" />
            </a>
          </div>
        </div>
      </article>

      {/* ============================================================
          SCENE 03 — THE DETAIL (vertical, image right, caption left)
          ============================================================ */}
      <article
        ref={setReveal(3)}
        className="scenes__scene scenes__scene--portrait"
        data-testid="scene-03"
      >
        <div className="scenes__split scenes__split--image-right">
          <div className="scenes__caption scenes__caption--beside scenes__caption--left">
            <span className="scenes__num">Scene 03</span>
            <h3 className="scenes__title">The Detail</h3>
            <p className="scenes__desc">
              Pattern, wood, and light meet at the scale of the hand.
            </p>
            <a
              href="/work/in-the-warm-after"
              className="scenes__view"
              data-testid="scene-03-link"
            >
              View Residence
              <span aria-hidden="true" className="scenes__view-rule" />
            </a>
          </div>
          <div className="scenes__mask scenes__mask--vertical">
            <PictureAsset
              base={BASES.details}
              alt="Pattern, wood grain and warm lamp light captured at close range"
              className="scenes__img"
              sizes="(min-width: 880px) 45vw, 100vw"
            />
          </div>
        </div>
      </article>

      {/* ============================================================
          SCENE 04 — THE EVERYDAY RITUAL (wide, caption below-left)
          ============================================================ */}
      <article
        ref={setReveal(4)}
        className="scenes__scene scenes__scene--wide"
        data-testid="scene-04"
      >
        <div className="scenes__mask scenes__mask--horizontal">
          <PictureAsset
            base={BASES.kitchen}
            alt="Warm working room with island, bar stools, plants, rhythmic wall panels and shelving"
            className="scenes__img"
            sizes="100vw"
          />
        </div>
        <div className="scenes__caption scenes__caption--below">
          <span className="scenes__num">Scene 04</span>
          <h3 className="scenes__title">The Everyday Ritual</h3>
          <p className="scenes__desc">
            A kitchen composed for storage and serving, and the everyday
            rituals that gather around the table.
          </p>
          <a
            href="/work/in-the-warm-after"
            className="scenes__view"
            data-testid="scene-04-link"
          >
            View Residence
            <span aria-hidden="true" className="scenes__view-rule" />
          </a>
        </div>
      </article>

      {/* ============================================================
          SCENE 05 — THE GATHERING (near full-width, release)
          ============================================================ */}
      <article
        ref={setReveal(5)}
        className="scenes__scene scenes__scene--release"
        data-testid="scene-05"
      >
        <div className="scenes__mask scenes__mask--release">
          <PictureAsset
            base={BASES.wide}
            alt="Open dining room with long table, dark doors, wall shelving, plants and pendant light"
            className="scenes__img"
            sizes="100vw"
          />
        </div>
        <div className="scenes__caption scenes__caption--below scenes__caption--release">
          <span className="scenes__num">Scene 05</span>
          <h3 className="scenes__title">The Gathering</h3>
          <p className="scenes__desc">
            A room composed for conversation, hosting, and lingering light.
          </p>
          <a
            href="/work/tie-and-untie"
            className="scenes__view"
            data-testid="scene-05-link"
          >
            View Residence
            <span aria-hidden="true" className="scenes__view-rule" />
          </a>
        </div>
      </article>

      {/* ============================================================
          CLOSING — VIEW ALL WORK
          ============================================================ */}
      <div
        ref={setReveal(6)}
        className="scenes__close"
        data-testid="view-all-work-section"
      >
        <a href="/work" className="scenes__view-all" data-testid="scenes-view-all-link">
          View All Work
          <svg
            className="scenes__view-all-arrow"
            width="22"
            height="10"
            viewBox="0 0 22 10"
            aria-hidden="true"
          >
            <path
              d="M1 5h20M17 1l4 4-4 4"
              stroke="currentColor"
              strokeWidth="1"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      </div>
    </section>
  );
}
